// A origem padrão em cadeia (design D2, revisão de 2026-10-13): sem escolha gravada, a curva
// segue o previsto do Prevision; sem ele, o Previsto do ERP; sem os dois, o
// orçamento, criado do zero. O estado do módulo diz qual fonte resolveu a origem e, para cada
// fonte da cadeia que ficou para trás, o motivo (`origin.discarded`).
//
// Os textos do módulo de desembolso que antes falavam de "previsto canônico" moram aqui, num
// lugar só, para o cabeçalho, o gráfico e o cartão da Projeção não divergirem.

export const UNAVAILABLE_HINT =
  "Esta obra não tem Previsto (do Prevision ou do ERP) nem orçamento para gerar a curva.";

// O nome de cada fonte da cadeia, como aparece no estado ("Origem: Previsto (ERP)").
export const ORIGIN_KIND_LABELS = Object.freeze({
  prevision: "Previsto (Prevision)",
  physical_financial: "Previsto (ERP)",
  erp_disbursement: "Desembolso do ERP",
  budget: "Orçamento",
});

// Por que uma fonte da cadeia não foi usada. Desconhecido → texto neutro, nunca o código cru.
export const DISCARD_REASON_LABELS = Object.freeze({
  prevision_not_enabled: "a obra não usa o Prevision",
  prevision_not_applied: "a obra não tem previsto do Prevision aplicado",
  prevision_without_service_curves: "o previsto aplicado não tem curva no nível de serviço",
  physical_financial_missing: "a obra não tem Previsto do ERP carregado",
  physical_financial_without_services: "a última medição do Previsto não tem serviços",
  planned_series_missing: "a obra não tem Previsto mensal carregado",
  mega_predicted_missing: "o MEGA ainda não publica o previsto mensal desta obra",
  budget_missing: "a obra não tem orçamento carregado",
  calculation_snapshot_unavailable: "a última carga de dados da obra está inconsistente",
});

const DISCARDED_SOURCE_NAMES = Object.freeze({
  prevision: "o previsto do Prevision",
  physical_financial: "o Previsto do ERP",
  erp_disbursement: "o desembolso do ERP",
  budget: "o orçamento",
});

const KINDS = Object.keys(ORIGIN_KIND_LABELS);
const LEGACY_PHYSICAL_LABEL = /f[ií]sico[- ]financeiro/i;

/**
 * A origem do estado (`ampliDisbursement.origin` do `/detail` ou `state.origin` do
 * `GET .../rules`): `{ kind, label, chosen, discarded }`. `kind` cai no `id` antigo da rota de
 * regras; sem nenhum dos dois, `null`.
 */
export const normalizeOriginState = (origin) => {
  if (!origin || typeof origin !== "object") return null;
  const kind = KINDS.includes(origin.kind) ? origin.kind : KINDS.includes(origin.id) ? origin.id : null;
  if (!kind) return null;
  const discarded = Array.isArray(origin.discarded)
    ? origin.discarded
        .filter((entry) => entry && KINDS.includes(entry.kind))
        .map((entry) => ({ kind: entry.kind, reason: entry.reason ?? null }))
    : [];
  return {
    kind,
    // Um estado gravado antes de 2026-10-15 traz "Físico-financeiro (...)": o produto diz
    // "Previsto" em toda a tela, então esse texto antigo cai no nome da fonte.
    label: typeof origin.label === "string" && origin.label && !LEGACY_PHYSICAL_LABEL.test(origin.label)
      ? origin.label
      : ORIGIN_KIND_LABELS[kind],
    chosen: origin.chosen === true,
    discarded,
  };
};

/** "Origem: Previsto (ERP), adotada por padrão." */
export const originStateText = (origin) =>
  origin
    ? `Origem: ${origin.label}, ${origin.chosen ? "escolhida para esta obra" : "adotada por padrão"}.`
    : null;

/** "Não usa o previsto do Prevision: a obra não tem previsto do Prevision aplicado." */
export const discardedText = ({ kind, reason }) =>
  `Não usa ${DISCARDED_SOURCE_NAMES[kind]}: ${DISCARD_REASON_LABELS[reason] ?? "fonte indisponível nesta obra"}.`;

// O nome do Previsto pela fonte (`physicalsSource` do `/detail`, `comparison.physical
// .source` da prévia): é o rótulo das legendas dos dois gráficos.
export const PHYSICAL_SOURCE_LABELS = Object.freeze({
  prevision: "Previsto (Prevision)",
  erp: "Previsto (ERP)",
  // Obra MEGA: a origem física é o previsto mensal do MEGA.
  mega: "Previsto (MEGA)",
});

/** Obra MEGA (`erp` do `/detail`): a origem física é o PREVISTO, do Prevision ou do MEGA. */
export const isMegaProject = (project) => String(project?.erp ?? "").toUpperCase() === "MEGA";

// A fonte do Previsto que a origem física da curva é: quando coincidem, o desembolso
// inicial É o Previsto, e os gráficos o desenham como uma série só.
export const PHYSICAL_SOURCE_OF_ORIGIN = Object.freeze({
  prevision: "prevision",
  physical_financial: "erp",
});

/** A origem física da curva é a mesma fonte da série da obra (`physical_financial` = ERP ou MEGA). */
export const originMatchesPhysicalSource = (originKind, source) => (
  originKind === "physical_financial"
    ? source === "erp" || source === "mega"
    : PHYSICAL_SOURCE_OF_ORIGIN[originKind] !== undefined && PHYSICAL_SOURCE_OF_ORIGIN[originKind] === source
);
